<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { config } from '@/config/config';

const { t } = useI18n();
const route = useRoute();

// #449 SUPERSEDES #8 D3. The tab used to be hidden when the location
// permission was 'denied', which was right while the view had nothing to
// offer without a GPS fix. #441 put a flight simulator there that needs no
// location access at all, so hiding the tab hid the one thing built for a
// pilot who has blocked location — or for anyone evaluating the product
// from a desk, which is the case the simulator exists for.
//
// Reachability is now the feature flag alone. The permission decides what
// the view can DO, not whether the pilot can get to it.
//
// PR #46 F5's `permissionSettled` gate goes with it. Its purpose was to
// avoid a flash of the tab for a previously-denied user, and there is no
// settled state that hides the tab any more — so all it could still do is
// shift the nav under the pilot's thumb a frame after paint, or hide the
// tab forever on an engine where `navigator.permissions.query` never
// settles. That is the exact unreachability this issue exists to remove.
//
// PR #55 R5 kept: the whole /tracking surface is gated behind
// config.features.contextualTracking (default false).
const navItems = computed(() => {
  const base = [
    { name: 'home', path: '/home', icon: 'home', label: 'home' },
    { name: 'saved', path: '/saved', icon: 'bookmark', label: 'savedRoutes' },
  ];
  if (config.features.contextualTracking) {
    base.push({ name: 'tracking', path: '/tracking', icon: 'tracking', label: 'tracking' });
  }
  base.push({ name: 'settings', path: '/settings', icon: 'settings', label: 'settings' });
  return base;
});
</script>

<template>
  <nav class="bg-deck-surface border-t border-deck-border pb-safe">
    <div class="flex justify-around items-center h-14">
      <router-link
        v-for="item in navItems"
        :key="item.name"
        :to="item.path"
        class="flex flex-col items-center justify-center flex-1 h-full font-mono text-nav font-bold uppercase transition-colors"
        :class="route.path === item.path ? 'text-brand' : 'text-deck-muted'"
      >
        <!-- Home icon -->
        <svg v-if="item.icon === 'home'" class="w-6 h-6 mb-pair" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>

        <!-- Bookmark icon -->
        <svg v-if="item.icon === 'bookmark'" class="w-6 h-6 mb-pair" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>

        <!-- Tracking icon (crosshair / radar — distinct from existing nav icons) -->
        <svg v-if="item.icon === 'tracking'" class="w-6 h-6 mb-pair" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2v3m0 14v3M2 12h3m14 0h3" />
          <circle cx="12" cy="12" r="8" stroke-width="2" />
          <circle cx="12" cy="12" r="3" stroke-width="2" />
        </svg>

        <!-- Settings icon -->
        <svg v-if="item.icon === 'settings'" class="w-6 h-6 mb-pair" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>

        <span>{{ t(item.label) }}</span>
      </router-link>
    </div>
  </nav>
</template>
